import React, { useState, ChangeEvent, FormEvent, useRef } from "react";

import SiteWrapper from "../../base/SiteWrapper";
import { Page, Button, Form, Card, Progress } from "tabler-react";
import { Alerta } from "../../utils/Alerta";

import { importSpreadsheet } from "../../utils/importSpreadsheet";
import {
  AvailableColumns,
  parseBrazilianDate,
  parseBRL,
  parseCustomerDocument,
  parseEmail,
  ParseError,
  parsePhoneNumber,
  requiredCols,
  Row,
} from "../../utils/cashbackTransactions/parseSpreadsheetData";
import { ApiFidegg } from "../../services/apiFideggAdmin";
import { Collapsible } from "../../components/Collapsible";

type ImportError = {
  line: number;
  col: AvailableColumns;
  message: string;
};

function CashbackSpreadsheetUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [importErrors, setImportErrors] = useState<ImportError[]>();

  const templateUrl = "/templates/relatorio-cashback-template.csv";

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  /**
   * 1- Lê o arquivo e converte em um formato padronizado
   */
  const parseSpreadsheet = async (file: File): Promise<Row[]> => {
    const parsedSheet = await importSpreadsheet(file);

    if (parsedSheet.rows.length === 0) {
      throw new Error("A planilha selecionada não possui nenhum registro!");
    }

    // Valida se o nome das colunas está correto
    for (const col of Object.keys(parsedSheet.rows[0])) {
      if (!Object.values(AvailableColumns).includes(col as AvailableColumns)) {
        throw new Error(`A coluna "${col}" não possui um nome válido.`);
      }
    }

    // Valida se todas colunas obrigatórias existem no documento
    for (const col of requiredCols) {
      if (!Object.keys(parsedSheet.rows[0]).includes(col)) {
        throw new Error(`A coluna "${col}" é obrigatória e não foi informada.`);
      }
    }

    return parsedSheet.rows.map((row) => ({
      order_code: row[AvailableColumns.CODIGO_PEDIDO],
      customer_document: parseCustomerDocument(row[AvailableColumns.CPF_CNPJ]),
      email: parseEmail(row[AvailableColumns.EMAIL]),
      cashback_value: parseBRL(
        AvailableColumns.VALOR_CASHBACK,
        row[AvailableColumns.VALOR_CASHBACK]
      ),
      expiration_date: parseBrazilianDate(
        AvailableColumns.DATA_EXPIRACAO,
        row[AvailableColumns.DATA_EXPIRACAO]
      ),
      approval_date: parseBrazilianDate(
        AvailableColumns.DATA_APROVACAO,
        row[AvailableColumns.DATA_APROVACAO]
      ),
      phone_number: parsePhoneNumber(row[AvailableColumns.TELEFONE]),
      purchase_value: parseBRL(
        AvailableColumns.VALOR_COMPRA,
        row[AvailableColumns.VALOR_COMPRA]
      ),
    }));
  };

  const valueProvided = (value: any) => {
    if (!value) {
      return false;
    } else if (value === ParseError.VALUE_NOT_PROVIDED) {
      return false;
    } else {
      return true;
    }
  };

  /**
   * 2 - Valida formatação, tipos de dados e colunas obrigatórias. Salva log dos erros para exibir ao usuário
   *     Validação no lado do cliente para evitar carga desnecessária ao enviar dados inválidos ao servidor
   */
  const validateRows = (rows: Row[]) => {
    const errors: ImportError[] = [];

    rows.forEach((r, i) => {
      const line = i + 2; // Excel header is line 1

      // Valida campos obrigatórios
      if (!valueProvided(r.customer_document)) {
        errors.push({
          line,
          col: AvailableColumns.CPF_CNPJ,
          message: `"${AvailableColumns.CPF_CNPJ}" não informado.`,
        });
      }

      if (!valueProvided(r.cashback_value)) {
        errors.push({
          line,
          col: AvailableColumns.VALOR_CASHBACK,
          message: `"${AvailableColumns.VALOR_CASHBACK}" não informado.`,
        });
      }

      if (!valueProvided(r.expiration_date)) {
        errors.push({
          line,
          col: AvailableColumns.DATA_EXPIRACAO,
          message: `"${AvailableColumns.DATA_EXPIRACAO}" não informado.`,
        });
      }

      // Validações específicas para o valor do cashback
      if (r.cashback_value) {
        // Formatacao fora do padrão 11200,00
        if (r.cashback_value === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.VALOR_CASHBACK,
            message: `"${AvailableColumns.VALOR_CASHBACK}" não segue o formato adequado.`,
          });
        } else if (
          typeof r.cashback_value !== "number" &&
          Number.isNaN(r.cashback_value)
        ) {
          errors.push({
            line,
            col: AvailableColumns.VALOR_CASHBACK,
            message: `"${AvailableColumns.VALOR_CASHBACK}" deve ser numérico.`,
          });
        } else if (
          typeof r.cashback_value === "number" &&
          r.cashback_value < 0
        ) {
          errors.push({
            line,
            col: AvailableColumns.VALOR_CASHBACK,
            message: `"${AvailableColumns.VALOR_CASHBACK}" não pode ser negativo.`,
          });
        }
      }

      // Validações específicas para o valor da compra
      if (r.purchase_value) {
        // Formatacao fora do padrão 11200,00
        if (r.purchase_value === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.VALOR_COMPRA,
            message: `"${AvailableColumns.VALOR_COMPRA}" não segue o formato adequado.`,
          });
        } else if (
          typeof r.cashback_value !== "number" &&
          Number.isNaN(r.cashback_value)
        ) {
          errors.push({
            line,
            col: AvailableColumns.VALOR_COMPRA,
            message: `"${AvailableColumns.VALOR_COMPRA}" deve ser numérico.`,
          });
        } else if (
          typeof r.cashback_value === "number" &&
          r.cashback_value < 0
        ) {
          errors.push({
            line,
            col: AvailableColumns.VALOR_COMPRA,
            message: `"${AvailableColumns.VALOR_COMPRA}" não pode ser negativo.`,
          });
        }
      }

      // Validações específicas para email
      if (r.email) {
        if (r.email === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.EMAIL,
            message: `"${AvailableColumns.EMAIL}" não possui um valor válido.`,
          });
        }
      }

      // Validações específicas para CPF/CNPJ
      if (r.customer_document) {
        if (r.customer_document === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.CPF_CNPJ,
            message: `"${AvailableColumns.CPF_CNPJ}" não possui um valor válido.`,
          });
        }
      }

      // TODO: adicionar validações sobre datas no passado?
      // Validações específicas para data de expiração
      if (r.expiration_date) {
        if (r.expiration_date === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.DATA_EXPIRACAO,
            message: `"${AvailableColumns.DATA_EXPIRACAO}" não possui um valor válido.`,
          });
        }
      }

      // Validações específicas para data de aprovação
      if (r.approval_date) {
        if (r.approval_date === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.DATA_APROVACAO,
            message: `"${AvailableColumns.DATA_APROVACAO}" não possui um valor válido.`,
          });
        }
      }

      // Validações específicas para telefone
      if (r.phone_number) {
        if (r.phone_number === ParseError.INVALID_FORMAT) {
          errors.push({
            line,
            col: AvailableColumns.TELEFONE,
            message: `"${AvailableColumns.TELEFONE}" não possui um valor válido.`,
          });
        }
      }
    });

    return errors;
  };

  /**
   * 3 - Cria chunks de dados para enviar os dados ao servidor em partes
   */
  const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  /**
   * 4 - Envia os dados ao servidor, um chunk de cada vez
   */
  const uploadChunks = async (chunks: any[][]) => {
    setUploading(true);
    setProgress(0);

    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];

      try {
        await ApiFidegg.post("cashbacktransactions/bulk", {
          data: chunk,
        });
      } catch (err: any) {
        setUploading(false);
        if (err.response) {
          const message = err.response.data.message;

          Alerta(`Erro ao salvar os dados da planilha. ${message}`, "error");
        } else {
          Alerta(`Erro ao salvar os dados da planilha`, "error");
        }
        return;
      }

      // update progress
      const percent = Math.round(((i + 1) / chunks.length) * 100);
      setProgress(percent);
    }

    setUploading(false);
    Alerta("Importação concluída com sucesso!", "success");
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!file) {
      Alerta("Selecione um arquivo antes de enviar.", "warning");
      return;
    }

    try {
      // Parse XLSX/CSV
      const rows = await parseSpreadsheet(file);

      // Validação
      const errors = validateRows(rows);
      setImportErrors(errors);

      if (errors.length > 0) {
        Alerta(
          `Foram encontrados ${errors.length} registros inválidos na planilha. \n
          Você pode conferir os detalhes clicando em "Detalhamento de erros" logo abaixo.
          `,
          "warning"
        );
        return;
      }

      // Chunking
      const chunks = chunkArray(rows, 300);

      // Upload
      await uploadChunks(chunks);
    } catch (err: any) {
      if (err.name === "NotReadableError") {
        Alerta(
          "O arquivo selecionado foi modificado. Por favor, selecione o arquivo novamente.",
          "warning"
        );
      } else {
        Alerta(err.message, "error");
      }
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
        setFile(null);
      }
    }
  };

  return (
    <SiteWrapper>
      <Form onSubmit={handleSubmit}>
        <Page.Card
          title="Importar Cashback em Massa"
          footer={
            <Card.Footer>
              <div className="d-flex align-items-center w-100">
                <Button
                  link
                  onClick={(e: any) => {
                    e.preventDefault();
                    window.history.back();
                  }}
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  className="ml-auto"
                  disabled={uploading}
                >
                  {uploading ? "Enviando..." : "Enviar"}
                </Button>
              </div>
            </Card.Footer>
          }
        >
          <Form.FieldSet>
            <Form.Group>
              {/* Explanation box */}
              <div className="p-3 bg-light rounded border mb-4">
                <h6 className="mb-3 text-primary font-weight-bold">
                  Estrutura obrigatória do arquivo CSV
                </h6>

                <p className="mb-2">
                  O arquivo pode conter as seguintes colunas, sendo que os
                  campos <b>CPF/CNPJ</b>, <b>Valor Cashback</b> e{" "}
                  <b>Data de Expiração</b> são obrigatórios.
                </p>
                <ul className="mb-3 text">
                  <li>CPF/CNPJ</li>
                  <li>E-mail</li>
                  <li>Valor Cashback</li>
                  <li>Valor Compra</li>
                  <li>Data de Expiração</li>
                  <li>Data de Aprovação</li>
                  <li>Código</li>
                  <li>Telefone</li>
                </ul>

                <p className="text-danger mb-2">
                  <b>Importante:</b> os nomes das colunas devem ser idênticos
                  aos da lista acima, e as datas devem seguir o formato
                  <b> dd/mm/yyyy</b>
                </p>

                <p className="mb-0">
                  O arquivo deve estar no formato <b>.CSV</b> com separação por
                  ponto e vírgula (<b>;</b>) OU no formato <b>.xlsx/.xls</b>.
                </p>
              </div>

              {/* File input */}
              <Form.Label>Selecione o arquivo</Form.Label>
              <Form.Input
                type="file"
                name="spreadsheet"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileChange}
                disabled={uploading}
                ref={fileInputRef}
              />

              {/* Template download */}
              {!file && (
                <div className="mt-2 text-muted">
                  Nenhum arquivo selecionado.{" "}
                  <a href={templateUrl} download>
                    Baixar modelo de cabeçalho (arquivo .csv)
                  </a>
                </div>
              )}
            </Form.Group>

            {importErrors && importErrors.length > 0 && (
              <Collapsible title="Detalhamento de Erros">
                {importErrors.map((i) => (
                  <div>
                    <p>
                      Linha {i.line}: {i.message}
                    </p>
                  </div>
                ))}
              </Collapsible>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="mt-4">
                <div className="mb-2 font-weight-bold">
                  Progresso do envio: {progress}%
                </div>
                <Progress size="sm" color="blue" value={progress} />
              </div>
            )}
          </Form.FieldSet>
        </Page.Card>
      </Form>
    </SiteWrapper>
  );
}

export default CashbackSpreadsheetUpload;
